import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import useSnackbar from '../../hooks/useSnackbar';
import Alert from '@mui/material/Alert';
import { usePostMutation } from '../../hooks/useAuthenticatedMutation';
import { ApiError } from '../../hooks/useAuthenticatedFetch';

const types = [
    'string',
    'number',
    'bool',
    'date',
    'phone',
    'email'
];

// todo type display name could be moved into types

const getType = type => {
    switch (type) {
        case 'string': return 'Text';
        case 'number': return 'Number';
        case 'bool': return 'Yes/No';
        case 'date': return 'Date';
        case 'phone': return 'Telephone Number';
        case 'email': return 'Email Address';
    }

    return type;
};

const sanitizeLive = (raw) =>
    raw.replace(/\s+/g, '-').replace(/-+/g, '-');

const sanitizeFinal = (raw) =>
    sanitizeLive(raw).replace(/^-+|-+$/g, '');

const AddFieldDialog = ({ onSubmit, onClose }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState(types[0]);
    const { showSnackbar } = useSnackbar();
    const postMutation = usePostMutation('custom-fields');

    const handleSubmitAddField = field => {
        const onSuccess = () => onSubmit(field);

        const onError = (error) => {
            const message = error instanceof ApiError && error.status === 409
                ? 'A Custom Field with this name already exists'
                : 'Error adding Custom Field';

            showSnackbar(message, 'error');
        };

        postMutation.mutate(field, { onSuccess, onError });
    };

    const handleChangeName = (e) => {
        setName(sanitizeLive(e.target.value));
    };

    return (
        <Dialog onClose={onClose} open={true}>
            <DialogTitle>Add Custom Field</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You are about to create a custom field. You can use these fields in your Contacts section and for personalising your email campaigns. Custom fields can also be created in your Contacts section during the Import process when using the Upload File option.
                </DialogContentText>
                <Alert severity="info" sx={{ my: 1 }}>Custom field names cannot contain spaces. They will be replaced with hyphens (-)</Alert>
                <TextField
                    autoFocus
                    id="name"
                    label="Name"
                    type="text"
                    fullWidth
                    value={name}
                    onChange={handleChangeName}
                    margin="dense"
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel>Data Type</InputLabel>
                    <Select value={type} onChange={e => setType(e.target.value)} label="Data Type">
                        {types.map(t => (
                            <MenuItem key={t} value={t}>
                                {getType(t)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        const santizeName = sanitizeFinal(name);
                        handleSubmitAddField({ name: santizeName, type });
                    }}
                    disabled={!name}
                    loading={postMutation.isPending}
                    loadingPosition="start"
                >
                    Add Field
                </Button>
                <Button variant="outlined" onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddFieldDialog;